import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useRef, useEffect } from "react";
import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { productsQuery, categoriesQuery, searchProductsQuery } from "@/lib/products";
import { ProductCard } from "@/components/product-card";
import { whatsappLink } from "@/lib/format";
import { useStoreSettings } from "@/lib/settings.functions";
import { requestOriginQuery } from "@/lib/seo.functions";
import type { Category } from "@/lib/products";

// Ids da própria categoria + de todas as suas subcategorias (recursivo, mas
// só há um nível de subcategoria hoje) — usado para o clique num chip de
// categoria de topo também trazer os produtos das subcategorias dela.
function descendantCategoryIds(categories: Category[], categoryId: string): string[] {
  const ids = [categoryId];
  for (const child of categories.filter((c) => c.parent_id === categoryId)) {
    ids.push(...descendantCategoryIds(categories, child.id));
  }
  return ids;
}

export const Route = createFileRoute("/")({
  loader: async ({ context }) => {
    context.queryClient.ensureQueryData(productsQuery());
    context.queryClient.ensureQueryData(categoriesQuery());
    const origin = await context.queryClient.ensureQueryData(requestOriginQuery());
    return { origin };
  },
  head: ({ loaderData }) => {
    const canonicalUrl = loaderData?.origin ? `${loaderData.origin}/` : undefined;
    return {
      meta: [
        { title: "Loja EG Elétrica — Painéis Elétricos" },
        {
          name: "description",
          content:
            "Catálogo de painéis elétricos residenciais, comerciais, industriais e sob medida. Compra online com segurança.",
        },
        { property: "og:title", content: "Loja EG Elétrica — Painéis Elétricos" },
        {
          property: "og:description",
          content:
            "Catálogo de painéis elétricos residenciais, comerciais, industriais e sob medida. Compra online com segurança.",
        },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(canonicalUrl ? [{ property: "og:url", content: canonicalUrl }] : []),
      ],
      links: canonicalUrl ? [{ rel: "canonical", href: canonicalUrl }] : [],
    };
  },
  component: Index,
});

function Index() {
  const { data: products } = useSuspenseQuery(productsQuery());
  const { data: categories } = useSuspenseQuery(categoriesQuery());
  const settings = useStoreSettings();
  const [activeSlug, setActiveSlug] = useState<string>("todos");
  const gridRef = useRef<HTMLDivElement>(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(searchInput), 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const isSearching = debouncedQuery.trim().length > 0;
  const { data: searchResults, isFetching: searching } = useQuery(
    searchProductsQuery(debouncedQuery),
  );

  const topLevelCategories = useMemo(() => categories.filter((c) => !c.parent_id), [categories]);
  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [categories, activeSlug],
  );
  // Resolve para a categoria de topo mesmo quando a ativa é uma subcategoria,
  // para a segunda linha de chips continuar visível ao navegar dentro dela.
  const activeTopLevel = useMemo(() => {
    if (!activeCategory) return undefined;
    const topLevelId = activeCategory.parent_id ?? activeCategory.id;
    return categories.find((c) => c.id === topLevelId);
  }, [categories, activeCategory]);
  const subcategories = useMemo(
    () => (activeTopLevel ? categories.filter((c) => c.parent_id === activeTopLevel.id) : []),
    [categories, activeTopLevel],
  );

  const matchingIds = useMemo(() => {
    if (activeSlug === "todos" || !activeCategory) return null;
    return new Set(descendantCategoryIds(categories, activeCategory.id));
  }, [activeSlug, activeCategory, categories]);

  const filtered = useMemo(() => {
    const baseList = isSearching ? (searchResults ?? []) : products;
    if (!matchingIds) return baseList;
    return baseList.filter((p) => p.category_id && matchingIds.has(p.category_id));
  }, [isSearching, searchResults, products, matchingIds]);

  const isPersonalizado = activeSlug === "personalizado";

  return (
    <div>
      <section className="bg-eg-dark text-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:py-24 text-center">
          <h1 className="font-display text-3xl sm:text-5xl font-extrabold text-white">
            Painéis Elétricos de <span className="text-eg-gold">Alta Qualidade</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/80">
            Residencial · Comercial · Industrial · Personalizado
          </p>
          <button
            type="button"
            onClick={() => gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="mt-8 inline-flex rounded-md bg-eg-gold px-6 py-3 font-semibold text-eg-dark hover:bg-eg-goldhover transition-colors"
          >
            Ver Catálogo
          </button>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-8" ref={gridRef}>
        <div className="relative mb-4 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar produtos..."
            className="w-full rounded-md border border-border bg-white py-2 pl-9 pr-9 text-sm outline-none transition-colors focus:border-eg-gold"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              aria-label="Limpar busca"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
          {[
            { slug: "todos", name: "Todos" },
            ...topLevelCategories.map((c) => ({ slug: c.slug, name: c.name })),
          ].map((opt) => {
            const active =
              activeSlug === opt.slug ||
              (opt.slug !== "todos" && activeTopLevel?.slug === opt.slug);
            return (
              <button
                key={opt.slug}
                type="button"
                onClick={() => setActiveSlug(opt.slug)}
                className={`whitespace-nowrap rounded-full border-2 border-eg-gold px-4 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-eg-gold text-eg-dark"
                    : "bg-transparent text-eg-goldtext hover:bg-eg-gold/10"
                }`}
              >
                {opt.name}
              </button>
            );
          })}
        </div>

        {subcategories.length > 0 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
            {[{ slug: activeTopLevel!.slug, name: "Todas" }, ...subcategories].map((opt) => {
              const active = activeSlug === opt.slug;
              return (
                <button
                  key={opt.slug}
                  type="button"
                  onClick={() => setActiveSlug(opt.slug)}
                  className={`whitespace-nowrap rounded-full border border-eg-gold/60 px-3 py-1 text-xs font-medium transition-colors ${
                    active
                      ? "bg-eg-gold/80 text-eg-dark"
                      : "bg-transparent text-eg-goldtext hover:bg-eg-gold/10"
                  }`}
                >
                  {opt.name}
                </button>
              );
            })}
          </div>
        )}

        {isPersonalizado && (
          <div className="mt-4 rounded-lg border border-eg-gold/40 bg-eg-gold/10 p-4 text-sm text-eg-dark">
            <strong>Painéis elétricos sob medida</strong> projetados especificamente para sua
            necessidade. Solicite seu orçamento gratuito pelo{" "}
            <a
              href={whatsappLink(
                settings.whatsapp_number,
                "Olá! Quero um orçamento para um painel personalizado.",
              )}
              className="font-semibold underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp
            </a>{" "}
            — respondemos em até 2 horas úteis.
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 pt-6">
        {isSearching && searching ? (
          <p className="py-12 text-center text-muted-foreground">Buscando...</p>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground">
            {isSearching
              ? `Nenhum produto encontrado para "${debouncedQuery}".`
              : "Nenhum produto nesta categoria ainda."}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
